import { useCallback, useLayoutEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';

/** Keep a little breathing room before the frame even at fractional glyph widths. */
const MARQUEE_EDGE_GUTTER_PX = 2;

export const SCROLLING_CARD_NAME_STYLES = `
  @keyframes cards-card-name-marquee {
    from { transform: translateX(0); }
    86%, 100% { transform: translateX(calc(-50% - 9px)); }
  }
  .cards-scrolling-card-name[data-marquee="true"] .cards-scrolling-card-name-track {
    animation: cards-card-name-marquee var(--dur-marquee) linear infinite;
  }
  .cards-scrolling-card-name-track > span { flex: 0 0 auto; }
  .cards-scrolling-card-name[data-multiline="true"] { white-space: normal; }
  .cards-scrolling-card-name[data-multiline="true"] .cards-scrolling-card-name-track {
    display: block; width: 100%; min-width: 0;
  }
  .cards-scrolling-card-name[data-multiline="true"] .cards-scrolling-card-name-track > span:first-child {
    display: -webkit-box; overflow: hidden; overflow-wrap: anywhere;
    -webkit-box-orient: vertical; -webkit-line-clamp: var(--cards-card-name-lines);
  }
  .cards-scrolling-card-name-word-measure {
    position: absolute; visibility: hidden; white-space: nowrap; pointer-events: none;
  }
  @media (prefers-reduced-motion: reduce) {
    .cards-scrolling-card-name-track { animation: none !important; }
  }
`;

export interface ScrollingCardNameProps {
  name: string;
  className?: string;
  trackClassName?: string;
  style?: CSSProperties;
  /**
   * false renders a static, never-marqueeing name with no measurement at all.
   * Used inside drag previews and flight ghosts: their box is resized every
   * animation frame, so a live ResizeObserver would force a synchronous
   * layout per frame and visibly drop the flight's frame rate.
   */
  animate?: boolean;
  /**
   * Lets a compact surface use the available height for a wrapped title.
   * When the title would exceed this many lines, it falls back to the usual
   * one-line marquee instead of clipping the name.
   */
  maxLines?: number;
}

/** Shared compact card name that wraps up to a supplied limit, then marquees on overflow. */
export function ScrollingCardName({
  name,
  className,
  trackClassName,
  style,
  animate = true,
  maxLines,
}: ScrollingCardNameProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const longestWordRef = useRef<HTMLSpanElement>(null);
  const longestWord = maxLines === undefined
    ? undefined
    : name.trim().split(/\s+/u).reduce((longest, word) => word.length > longest.length ? word : longest, '');
  // Keeps SSR and geometry-free test DOM deterministic until a real measurement is available.
  const [marquee, setMarquee] = useState(animate && (maxLines === undefined || name.length > 12));

  const measure = useCallback((): void => {
    if (!animate) return;
    const container = containerRef.current;
    const text = textRef.current;
    if (container === null || text === null) return;
    const containerWidth = container.clientWidth || container.getBoundingClientRect().width;
    // `scrollWidth` is rounded to whole pixels. Preserve the fractional layout
    // width too: a title that exceeds its container by less than one pixel must
    // still be clipped and marquee rather than bleed into the card frame.
    const textWidth = Math.max(text.scrollWidth, text.getBoundingClientRect().width);
    if (containerWidth <= 0) {
      setMarquee(maxLines === undefined ? name.length > 12 : true);
      return;
    }
    const longestWordElement = longestWordRef.current;
    const longestWordWidth = longestWordElement === null
      ? 0
      : Math.max(longestWordElement.scrollWidth, longestWordElement.getBoundingClientRect().width);
    setMarquee(
      textWidth + MARQUEE_EDGE_GUTTER_PX > containerWidth * (maxLines ?? 1)
      || longestWordWidth + MARQUEE_EDGE_GUTTER_PX > containerWidth,
    );
  }, [name, animate, maxLines]);

  useLayoutEffect(() => {
    if (!animate) return;
    measure();
    window.addEventListener('resize', measure);
    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    if (containerRef.current !== null) observer.observe(containerRef.current);
    if (textRef.current !== null) observer.observe(textRef.current);
    if (longestWordRef.current !== null) observer.observe(longestWordRef.current);
    // The text can become wider after the display font finishes loading without
    // changing its container's dimensions, which ResizeObserver cannot detect.
    void document.fonts?.ready.then(measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  return (
    <div
      ref={containerRef}
      className={['cards-scrolling-card-name', className].filter(Boolean).join(' ')}
      data-marquee={marquee ? 'true' : 'false'}
      data-multiline={maxLines !== undefined && !marquee ? 'true' : undefined}
      style={{
        ...style,
        ...(maxLines === undefined ? {} : { '--cards-card-name-lines': maxLines }),
      } as CSSProperties}
    >
      <span
        className={['cards-scrolling-card-name-track', trackClassName].filter(Boolean).join(' ')}
      >
        <span ref={textRef}>{name}</span>
        {marquee ? <span aria-hidden="true">{name}</span> : null}
      </span>
      {longestWord === undefined ? null : (
        <span ref={longestWordRef} className="cards-scrolling-card-name-word-measure" aria-hidden="true">
          {longestWord}
        </span>
      )}
    </div>
  );
}
